import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays, startOfDay } from 'date-fns';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { getMetrics } from '@/services/api';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Select from '@/components/common/Select';
import MetricCard from '@/components/charts/MetricCard';
import { formatNumber, formatCurrency } from '@/utils/formatters';
import {
  UsersIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

export default function Analytics() {
  const [dateRange, setDateRange] = useState('30');

  const endDate = format(startOfDay(new Date()), 'yyyy-MM-dd');
  const startDate = format(subDays(startOfDay(new Date()), parseInt(dateRange)), 'yyyy-MM-dd');

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['metrics', startDate, endDate],
    queryFn: () => getMetrics(startDate, endDate),
  });

  const dateRangeOptions = [
    { value: '7', label: 'Last 7 days' },
    { value: '14', label: 'Last 14 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
  ];

  // Calculate totals and averages
  const totals = metrics?.reduce(
    (acc, m) => ({
      newUsers: acc.newUsers + m.new_users,
      activeUsers: acc.activeUsers + m.active_users,
      newSubscriptions: acc.newSubscriptions + m.new_subscriptions,
      messages: acc.messages + m.total_messages,
      searches: acc.searches + m.total_searches,
    }),
    { newUsers: 0, activeUsers: 0, newSubscriptions: 0, messages: 0, searches: 0 }
  ) || { newUsers: 0, activeUsers: 0, newSubscriptions: 0, messages: 0, searches: 0 };

  const latestMetrics = metrics?.[metrics.length - 1];

  // Format chart data
  const chartData = metrics?.map((m) => ({
    date: format(new Date(m.date), 'MMM d'),
    users: m.total_users,
    newUsers: m.new_users,
    activeUsers: m.active_users,
    mrr: m.mrr_cents / 100,
    messages: m.total_messages,
    searches: m.total_searches,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Platform metrics and performance data
          </p>
        </div>
        <Select
          value={dateRange}
          onChange={setDateRange}
          options={dateRangeOptions}
          className="w-40"
        />
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={formatNumber(latestMetrics?.total_users || 0)}
          icon={<UsersIcon className="w-5 h-5" />}
          changeLabel={`+${totals.newUsers} in period`}
          loading={isLoading}
        />
        <MetricCard
          title="Total Businesses"
          value={formatNumber(latestMetrics?.total_businesses || 0)}
          icon={<BuildingStorefrontIcon className="w-5 h-5" />}
          changeLabel={`+${totals.newSubscriptions} subscriptions`}
          loading={isLoading}
        />
        <MetricCard
          title="Monthly Revenue"
          value={formatCurrency((latestMetrics?.mrr_cents || 0))}
          icon={<CurrencyDollarIcon className="w-5 h-5" />}
          loading={isLoading}
        />
        <MetricCard
          title="Total Messages"
          value={formatNumber(totals.messages)}
          icon={<ChatBubbleLeftRightIcon className="w-5 h-5" />}
          changeLabel="in period"
          loading={isLoading}
        />
      </div>

      {/* User Growth Chart */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">User Growth</h2>
        </CardHeader>
        <CardBody>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="users"
                  name="Total Users"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="activeUsers"
                  name="Active Users"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Users Chart */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">New User Signups</h2>
          </CardHeader>
          <CardBody>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="newUsers" name="New Users" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>

        {/* Revenue Chart */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Monthly Recurring Revenue</h2>
          </CardHeader>
          <CardBody>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip formatter={(value) => [`$${value}`, 'MRR']} />
                  <Line
                    type="monotone"
                    dataKey="mrr"
                    name="MRR"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Engagement Chart */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Engagement Metrics</h2>
        </CardHeader>
        <CardBody>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="messages" name="Messages" fill="#2563eb" radius={[4, 4, 0, 0]} />
                <Bar dataKey="searches" name="Searches" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
